"use client";

import { m, useInView } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import SectionHeader from "../sectionHeader";

interface FrameworkItem {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  practiceTitle: string;
  points: string[];
}

interface Props {
  data?: {
    title?: string;
    headline?: string;
    items: FrameworkItem[];
  };
}

const itemVariants = {
  initial: { opacity: 0, y: 32 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      type: "spring",
    },
  },
};

export default function FrameworkSection({ data }: Props) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <m.section
      ref={ref}
      className="section"
      transition={{ staggerChildren: 0.05, delayChildren: 0.2 }}
      initial="initial"
      animate={isInView ? "animate" : "initial"}
    >
      <div className="container mx-auto flex flex-col">
        <SectionHeader title={data?.title} headline={data?.headline} />
        {data?.items?.map((item, index) => (
          <m.div
            key={index}
            variants={itemVariants}
            className="flex flex-col gap-6 py-14 "
          >
            {/* Heading row */}
            <div className="flex items-center gap-4">
              <span className="frame flex items-center justify-center w-10 h-10 mono">{item.number}</span>
              <h3 className="h3 uppercase">{item.title}</h3>
            </div>

            {/* Grid: left = subtitle + description, right = practice (if exists) */}
            <div className={`grid gap-8 md:gap-16 ${item.practiceTitle || item.points.length > 0 ? "md:grid-cols-2" : ""}`}>
              <div className="flex flex-col gap-4">
                {item.subtitle && <p className="h4 uppercase">{item.subtitle}</p>}
                <p className="text-base leading-relaxed">{item.description}</p>
              </div>

              {(item.practiceTitle || item.points.length > 0) && (
                <div className="flex flex-col gap-6">
                  {item.practiceTitle && <h4 className="h4 uppercase">{item.practiceTitle}</h4>}
                  {item.points.length > 0 && (
                    <ul className="flex flex-col gap-3">
                      {item.points.map((point, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Image src="/images/arrow-red.svg" alt="Framework point" width={16} height={20} aria-hidden="true" className="shrink-0 mt-0.5 rotate-90" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </m.div>
        ))}
      </div>
    </m.section>
  );
}
